import { useEffect, type MouseEvent as ReactMouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getArticleBySlug } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';
import JsonLd from '@/seo/JsonLd';
import { buildArticleSchema } from '@/seo/schema';

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').split('/')[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = getArticleBySlug(slug || '');

  if (!article) {
    return <NotFound />;
  }

  return (
    <article className="min-h-screen bg-background-50">
      <JsonLd
        data={buildArticleSchema({
          title: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          path: `/${slug}`,
        })}
      />
      <ArticleNav />
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <Breadcrumb title={article.title} />
        <ArticleHeader article={article} />
        <ArticleContent article={article} />
        <ArticleFAQ article={article} />
        <ArticleFooter article={article} />
      </main>
      <ArticleFoot />
    </article>
  );
}

function ArticleNav() {
  return (
    <nav className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-line text-background-50 text-xs"></i>
            </div>
            <span className="font-heading font-semibold text-foreground-950">BasetaoSheet</span>
          </Link>
          <Link
            to="/"
            className="hidden sm:flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600 transition-colors"
          >
            <i className="ri-home-4-line"></i>
            Home
          </Link>
          <Link
            to="/basetao-spreadsheet-guide"
            className="hidden sm:flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600 transition-colors"
          >
            <i className="ri-book-read-line"></i>
            Shopping Guide
          </Link>
        </div>
        <a
          href="https://kaspreadsheet.com"
          target="_blank"
          rel="noopener nofollow"
          className="px-4 py-2 rounded-lg text-xs font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          basetao spreadsheet
        </a>
      </div>
    </nav>
  );
}

function Breadcrumb({ title }: { title: string }) {
  return (
    <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="Breadcrumb">
      <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
      <i className="ri-arrow-right-s-line text-[10px]"></i>
      <span className="text-foreground-700 font-medium">{title}</span>
    </nav>
  );
}

function ArticleHeader({ article }: { article: { title: string; excerpt: string; readTime: string; date: string; category: string } }) {
  return (
    <header className="mb-10">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">{article.category}</span>
        <span className="text-xs text-foreground-400">{article.readTime}</span>
        <span className="text-xs text-foreground-400">{article.date}</span>
      </div>
      <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
        {article.title}
      </h1>
      <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
        {article.excerpt}
      </p>
    </header>
  );
}

function ArticleContent({ article }: { article: { sections: { heading: string; content: string; subSections?: { heading: string; content: string }[] }[] } }) {
  const navigate = useNavigate();

  const handleContentClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href && href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div onClick={handleContentClick}>
      {article.sections.map((section, idx) => (
        <section key={idx} className="mb-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">{section.heading}</h2>
          <div
            className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
          {section.subSections?.map((sub, subIdx) => (
            <div key={subIdx} className="mt-4">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">{sub.heading}</h3>
              <div
                className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4"
                dangerouslySetInnerHTML={{ __html: sub.content }}
              />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function ArticleFAQ({ article }: { article: { faqs: { question: string; answer: string }[] } }) {
  if (!article.faqs || article.faqs.length === 0) return null;

  return (
    <section className="mb-10">
      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">Frequently Asked Questions</h2>
      <div className="space-y-5">
        {article.faqs.map((faq, idx) => (
          <div key={idx}>
            <h3 className="font-semibold text-foreground-900 text-sm mb-2">{faq.question}</h3>
            <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{faq.answer}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ArticleFooter({ article }: { article: { title: string } }) {
  return (
    <footer className="mt-12 pt-8 border-t border-background-200/70">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs text-foreground-400 mb-1">Last updated: {new Date().toISOString().split('T')[0]}</p>
          <p className="text-xs text-foreground-400">Content reviewed by the BasetaoSheet editorial team</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-400">Share:</span>
          <a href="#" className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
            <i className="ri-twitter-x-line text-xs text-foreground-600"></i>
          </a>
          <a href="#" className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
            <i className="ri-reddit-line text-xs text-foreground-600"></i>
          </a>
          <a href="#" className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
            <i className="ri-link text-xs text-foreground-600"></i>
          </a>
        </div>
      </div>

      <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div>
            <h4 className="font-heading font-semibold text-foreground-950 text-sm mb-1">Ready to start shopping smarter?</h4>
            <p className="text-xs text-foreground-500">Access organized product data and discover better finds on the main Basetao platform.</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-lg text-xs font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              basetao spreadsheet
            </a>
            <Link
              to="/"
              className="px-5 py-2.5 rounded-lg text-xs font-semibold bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function ArticleFoot() {
  return (
    <div className="bg-foreground-950 text-background-50/70">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-table-line text-background-50 text-[10px]"></i>
          </div>
          <span className="font-heading font-semibold text-sm text-background-50">BasetaoSheet</span>
        </Link>
        <p className="text-xs text-background-50/40">
          &copy; {new Date().getFullYear()} BasetaoSheet.club. An independent resource.
        </p>
      </div>
    </div>
  );
}